import { SECTION_IDS, type SectionId } from '../types';

/*
 * МАРШРУТ НА ТЕЛЕФОНЕ.
 *
 * Акт 0 остаётся обычной прокруткой — на ней он работает хорошо, и его никто
 * не трогает. А вот маршрут прокруткой не ведётся: длинные анимации,
 * растянутые по экранам, на телефоне превращаются в невыверенную по таймингу
 * кашу, и любое микрокасание сбивает кадр.
 *
 * Поэтому здесь другая модель, и она умышленно простая:
 *
 *   • один раздел — один экран, в уже досмотренном виде, без анимаций;
 *   • импульс вниз любой длины или стрелка — следующий раздел;
 *   • импульс вверх первый — показать шапку, второй — предыдущий раздел;
 *   • на первом разделе второй импульс вверх возвращает в Акт 0.
 *
 * Единственное исключение — уход в подвал: он короткий и его стоит увидеть,
 * поэтому последний переход не прыгает, а проигрывается целиком.
 *
 * Пилот не считает положение маршрута сам. Он двигает документ туда, где
 * маршрут его и так ждёт (`marketingPhysicalInput` в index.html), поэтому вся
 * остальная машинерия — партитура, миры, блоки — работает без единой правки.
 */

/** Сколько пикселей пальца считаются намерением, а не дрожанием при касании. */
const IMPULSE_PX = 16;
/**
 * Уход в подвал — единственная анимация маршрута, которую видно целиком.
 * У режиссёра на компьютере эта глава длится 8 секунд; 2,6 секунды читались
 * как обрыв — картинка не успевала дойти и подменялась последним кадром.
 */
const FINALE_MS = 4200;
/** Возврат из первого раздела в Акт 0 — тем же движением, что и переход. */
const RELEASE_MS = 520;

const smooth = (value: number): number => {
  const t = Math.max(0, Math.min(1, value));
  return t * t * (3 - 2 * t);
};

export interface MobileRouteState {
  readonly active: boolean;
  readonly index: number;
  readonly sectionId: SectionId;
  readonly motion: 'full' | 'reduced';
  readonly headerVisible: boolean;
  readonly last: boolean;
}

interface Tween { from: number; to: number; at: number; ms: number; finale: boolean }

export class MobileRoutePilot {
  /*
   * У раздела `where` нет собственного содержания: это хвост той же сцены, что
   * и «Создавай сцены в 3D» — тот же заголовок и та же пара окон. Отдельным
   * экраном он выглядел пустым: замерено — в кадре оставался один кубик.
   */
  readonly steps: readonly SectionId[] = SECTION_IDS.filter(id => id !== 'where');
  private index = 0;
  private engaged = false;
  private headerShown = false;
  private tween: Tween | null = null;
  private touchY = 0;
  private touchX = 0;
  private gestureUsed = false;
  private readonly cue: HTMLButtonElement;
  private readonly abort = new AbortController();
  private frameTime = 0;
  /* Адрес раздела из ссылки: применяется на первом же кадре, когда маршрут
     уже измерен. Без этого возврат из «Политики конфиденциальности» кнопкой
     «назад» выбрасывал в начало маршрута, а не туда, откуда ушли. */
  private pending: SectionId | null = null;

  constructor(private readonly view: Window, private readonly root: HTMLElement) {
    const doc = root.ownerDocument;
    this.cue = doc.createElement('button');
    this.cue.className = 'cinema-cue';
    this.cue.type = 'button';
    this.cue.hidden = true;
    this.cue.dataset.arrowOnly = 'true';
    this.cue.setAttribute('aria-label', 'Следующий раздел');
    this.cue.innerHTML = '<span hidden></span><svg viewBox="0 0 24 24" aria-hidden="true"><path d="M12 4v16m-6-6 6 6 6-6"/></svg>';
    doc.body.append(this.cue);

    const signal = this.abort.signal;
    this.cue.addEventListener('click', () => this.next(), { signal });

    /* Поля ввода и языковой список живут своей жизнью: там палец принадлежит
       содержимому, а не маршруту.
       Роликов в этом списке намеренно нет. Они занимают середину экрана, и
       пока они его перехватывали, раздел «Собирай анимации и 3D видео»
       становился тупиком: замерено — девять махов подряд не сдвигали кадр.
       Нажатие на ролик при этом работает: отменяется только touchmove. */
    const interactive = (target: EventTarget | null): boolean =>
      target instanceof Element
      && Boolean(target.closest('input,textarea,select,.lang,[role="listbox"],[data-native-scroll]'));

    this.view.addEventListener('touchstart', event => {
      this.touchY = event.touches[0]?.clientY ?? 0;
      this.touchX = event.touches[0]?.clientX ?? 0;
      this.gestureUsed = false;
    }, { passive: true, signal });

    this.view.addEventListener('touchmove', event => {
      if (!this.engaged || interactive(event.target)) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      if (this.gestureUsed) return;
      const y = event.touches[0]?.clientY ?? this.touchY;
      const x = event.touches[0]?.clientX ?? this.touchX;
      const dy = this.touchY - y;
      if (Math.abs(dy) < IMPULSE_PX || Math.abs(x - this.touchX) > Math.abs(dy)) return;
      /* Один мах — одно намерение. Иначе длинный свайп проматывал бы три
         раздела подряд, а именно от этого маршрут и уводили. */
      this.gestureUsed = true;
      if (dy > 0) this.next(); else this.back();
    }, { capture: true, passive: false, signal });

    this.view.addEventListener('touchend', () => { this.gestureUsed = false; }, { passive: true, signal });

    this.view.addEventListener('wheel', event => {
      if (!this.engaged || interactive(event.target) || event.ctrlKey) return;
      if (Math.abs(event.deltaX) > Math.abs(event.deltaY)) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      if (this.gestureUsed || Math.abs(event.deltaY) < 4) return;
      this.gestureUsed = true;
      this.view.setTimeout(() => { this.gestureUsed = false; }, 420);
      if (event.deltaY > 0) this.next(); else this.back();
    }, { capture: true, passive: false, signal });

    this.view.addEventListener('keydown', event => {
      if (!this.engaged || interactive(event.target)) return;
      if (event.target instanceof Element && event.target.closest('button,a')) return;
      if (['ArrowDown', 'PageDown', ' '].includes(event.key)) { event.preventDefault(); this.next(); }
      else if (['ArrowUp', 'PageUp'].includes(event.key)) { event.preventDefault(); this.back(); }
    }, { capture: true, signal });

    const linked = this.view.location.hash.slice(1) as SectionId;
    if (this.steps.includes(linked)) this.pending = linked;
  }

  /** Адрес текущего раздела — чтобы возврат «назад» приводил именно сюда. */
  private mark(): void {
    const id = this.steps[this.index]!;
    if (this.view.location.hash.slice(1) === id) return;
    this.view.history.replaceState(this.view.history.state, '', `#${id}`);
  }

  /** Где в документе начинается маршрут. Это число считает index.html, не мы. */
  private base(): number {
    const film = this.root.ownerDocument.getElementById('film');
    return (film?.offsetTop ?? 0) + this.view.BB_FILM_SCORE.endVh * this.view.innerHeight;
  }

  /*
   * Куда встать, чтобы раздел оказался ровно в кадре.
   *
   * Берётся не «номер × экран», а настоящее положение раздела внутри маршрута:
   * ровно из него маршрут считает свой ход (`travelPx`). Раздел выше экрана
   * сдвигает всех соседей, и на арифметике по номеру кадр уезжал в пустоту —
   * замерено: вместо разделов был виден один кубик.
   */
  private stop(index: number): number {
    const clamped = Math.max(0, Math.min(this.steps.length - 1, index));
    /*
     * Подвал — конец документа, а не начало своего раздела.
     *
     * Его анимация считается от доли прохода раздела, и на начале раздела эта
     * доля равна половине: замерено — подвал оставался на нулевой видимости,
     * а в кадре стояли одни объекты финала. Доехав до самого низа документа,
     * доля становится единицей, и финал доигрывается до конца.
     */
    if (clamped === this.steps.length - 1) {
      const doc = this.root.ownerDocument.documentElement;
      return Math.max(0, doc.scrollHeight - this.view.innerHeight);
    }
    const node = this.root.querySelector<HTMLElement>(`[data-section-id="${this.steps[clamped]!}"]`);
    return this.base() + (node?.offsetTop ?? clamped * this.view.innerHeight);
  }

  get state(): MobileRouteState {
    return {
      active: this.engaged,
      index: this.index,
      sectionId: this.steps[this.index]!,
      /* Маршрут показывается в досмотренном виде. Акт 0 сюда не относится —
         он играется целиком, — и уход в подвал тоже: он короткий и его стоит
         увидеть. */
      motion: !this.engaged || this.tween?.finale || this.index >= this.steps.length - 1 ? 'full' : 'reduced',
      /* На последнем экране шапка открыта сразу: дальше листать некуда, а
         уйти в другой раздел человеку по-прежнему нужно. */
      headerVisible: !this.engaged || this.headerShown || this.index >= this.steps.length - 1,
      last: this.index >= this.steps.length - 1,
    };
  }

  next(): void {
    if (!this.engaged || this.tween) return;
    if (this.index >= this.steps.length - 1) return;
    const finale = this.index === this.steps.length - 2;
    this.index += 1;
    this.headerShown = false;
    this.tween = { from: this.view.scrollY, to: this.stop(this.index), at: this.frameTime, ms: finale ? FINALE_MS : 0, finale };
  }

  /** Переход по ссылке в шапке: без проигрывания, как и обычный шаг. */
  goTo(id: string): boolean {
    const index = this.steps.indexOf(id as SectionId);
    if (index < 0) return false;
    if (!this.engaged) { this.engaged = true; this.index = index; this.view.scrollTo({ top: this.stop(index), behavior: 'instant' }); return true; }
    if (index === this.index) return true;
    this.headerShown = false;
    this.index = index;
    this.tween = { from: this.view.scrollY, to: this.stop(index), at: this.frameTime, ms: 0, finale: false };
    return true;
  }

  /** Первый импульс вверх открывает шапку, второй уводит назад. */
  back(): void {
    if (!this.engaged || this.tween) return;
    if (!this.headerShown) { this.headerShown = true; return; }
    this.headerShown = false;
    if (this.index === 0) { this.release(); return; }
    this.index -= 1;
    this.tween = { from: this.view.scrollY, to: this.stop(this.index), at: this.frameTime, ms: 0, finale: false };
  }

  /** Логотип в шапке: вернуться на главный экран и отдать палец Акту 0. */
  toFilm(): void {
    this.engaged = false;
    this.index = 0;
    this.headerShown = false;
    this.tween = null;
    /* Уводим документ сразу. Плавная прокрутка снаружи не годится: пока она
       идёт, положение всё ещё ниже начала маршрута, и ближайший же кадр
       подхватывал палец обратно — замерено, логотип возвращал на первый
       раздел маршрута вместо главного экрана. */
    this.view.scrollTo({ top: 0, behavior: 'instant' });
  }

  /** Отпустить документ обратно в Акт 0: маршрут снова не владеет пальцем. */
  private release(): void {
    this.engaged = false;
    this.headerShown = false;
    this.tween = { from: this.view.scrollY, to: Math.max(0, this.base() - this.view.innerHeight), at: this.frameTime, ms: RELEASE_MS, finale: false };
  }

  tick(now: number): void {
    this.frameTime = now;
    const base = this.base();

    if (this.pending) {
      const target = this.pending;
      this.pending = null;
      this.goTo(target);
    }

    if (this.tween) {
      const { from, to, at, ms } = this.tween;
      const t = ms <= 0 ? 1 : Math.min(1, (now - at) / ms);
      this.view.scrollTo({ top: from + (to - from) * smooth(t), behavior: 'instant' });
      if (t >= 1) this.tween = null;
    } else if (this.engaged) {
      /* Маршрут удерживает документ на своём разделе: инерция телефона иначе
         уводит кадр на полэкрана после каждого маха. */
      const target = this.stop(this.index);
      if (Math.abs(this.view.scrollY - target) > 1) this.view.scrollTo({ top: target, behavior: 'instant' });
    } else if (this.view.scrollY >= base - 1) {
      /* Акт 0 закончился — маршрут принимает палец на себя. */
      this.engaged = true;
      this.index = 0;
      this.headerShown = false;
      this.view.scrollTo({ top: base, behavior: 'instant' });
    }

    if (this.engaged) this.mark();
    this.root.dataset.mobileRoute = this.engaged ? this.state.sectionId : 'film';
    this.root.dataset.mobileRouteActive = String(this.engaged);
    this.cue.hidden = !this.engaged || this.state.last || Boolean(this.tween);

    /* Ровно один раздел в кадре: соседи не должны выглядывать снизу и сверху. */
    for (const node of this.root.querySelectorAll<HTMLElement>('[data-section-id]')) {
      const permitted = this.engaged && node.id === this.state.sectionId;
      const visibility = permitted ? '' : 'hidden';
      if (node.style.visibility !== visibility) node.style.visibility = visibility;
      if (node.inert === permitted) node.inert = !permitted;
    }
  }

  destroy(): void {
    this.abort.abort();
    this.cue.remove();
    delete this.root.dataset.mobileRoute;
    delete this.root.dataset.mobileRouteActive;
    for (const node of this.root.querySelectorAll<HTMLElement>('[data-section-id]')) {
      node.style.removeProperty('visibility');
      node.inert = false;
    }
    if (this.view.__BB_MOBILE_ROUTE__ === this) delete this.view.__BB_MOBILE_ROUTE__;
  }
}

declare global { interface Window { __BB_MOBILE_ROUTE__?: MobileRoutePilot } }

export function mountMobileRoute(view: Window, root: HTMLElement): MobileRoutePilot {
  return view.__BB_MOBILE_ROUTE__ ?? (view.__BB_MOBILE_ROUTE__ = new MobileRoutePilot(view, root));
}
